import { useState } from "react";
import { Link } from "react-router-dom";
import { FOOTER_LINKS, COMPANY } from "@/lib/site-data";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Linkedin, Twitter, Facebook, Instagram, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Subscribed. Welcome to Agroya updates.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-white border-t border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3">
            <img
              src={COMPANY.logo}
              alt="Agroya Refinery"
              className="w-11 h-11 rounded-full object-contain"
              style={{ filter: "drop-shadow(0 4px 12px rgba(212,175,55,0.25))" }}
            />
            <div className="leading-tight">
              <div className="font-heading text-base font-medium text-[#0A0A0A]">Agroya Refinery LLP</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Pune · India</div>
            </div>
          </div>
          <p className="mt-5 text-sm text-[#4B5563] leading-relaxed max-w-sm">
            A professionally managed precious metals refining company delivering high-purity gold and silver
            products with international quality standards and complete transparency.
          </p>
          <form onSubmit={subscribe} className="mt-6 flex gap-2 max-w-sm">
            <Input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email" placeholder="Subscribe to updates"
              className="bg-[#F4F9FC]"
            />
            <Button type="submit" data-testid="newsletter-submit" disabled={loading} className="btn-gold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
            </Button>
          </form>
          <div className="mt-6 flex items-center gap-3">
            {[Linkedin, Twitter, Facebook, Instagram].map((Icon, i) => (
              <a key={i} href="#" data-testid={`social-${i}`} className="w-9 h-9 rounded-full border border-[#E5E5E5] hover:border-[#D4AF37] flex items-center justify-center transition-colors">
                <Icon className="w-4 h-4 text-[#0A0A0A]" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>

        {Object.entries(FOOTER_LINKS).map(([group, links]) => (
          <div key={group} className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">{group}</div>
            <ul className="mt-5 space-y-3">
              {links.map((l) => (
                <li key={l.label}>
                  {l.href.startsWith("/#") ? (
                    <a href={l.href} className="text-sm text-[#0A0A0A]/80 hover:text-[#D4AF37] transition-colors">{l.label}</a>
                  ) : (
                    <Link to={l.href} className="text-sm text-[#0A0A0A]/80 hover:text-[#D4AF37] transition-colors">{l.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">Reach Us</div>
          <ul className="mt-5 space-y-3 text-sm text-[#4B5563]">
            <li>{COMPANY.phone}</li>
            <li>{COMPANY.email}</li>
            <li>{COMPANY.hours}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-[#4B5563]">© {new Date().getFullYear()} Agroya Refinery LLP. All rights reserved.</div>
          <div className="flex items-center gap-5 text-xs text-[#4B5563]">
            <Link to="/privacy" className="hover:text-[#D4AF37]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#D4AF37]">Terms</Link>
            <a href="#contact" className="hover:text-[#D4AF37]">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
